import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, ShieldCheck, FileText, Briefcase, ArrowRight } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { insights, caseStudies } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberPortal = () => {
  useSeo({ title: "Member Portal", path: "/member" });
  const navigate = useNavigate();
  const location = useLocation();
  // Skip /me check if we already have user data from AuthCallback state or from
  // the legacy JWT flow in localStorage.
  const [user, setUser] = useState(() => {
    if (location.state?.user) return location.state.user;
    const raw = localStorage.getItem("astoria_member_user");
    if (raw) {
      try { return JSON.parse(raw); } catch { /* fall through */ }
    }
    return null;
  });
  const [loading, setLoading] = useState(!user);

  useEffect(() => {
    if (user) return;
    // No local user — try to hydrate from the Google session cookie.
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser({
          username: data.name || data.email,
          email: data.email,
          picture: data.picture,
          auth_provider: data.auth_provider,
        });
      } catch {
        navigate("/member-login", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [user, navigate]);

  const signOut = async () => {
    if (localStorage.getItem("astoria_member_token")) {
      localStorage.removeItem("astoria_member_token");
      localStorage.removeItem("astoria_member_user");
    }
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    navigate("/");
  };

  if (loading) {
    return (
      <section className="min-h-[70vh] flex items-center justify-center bg-neutral-50">
        <p className="text-sm text-neutral-500">Loading your portal…</p>
      </section>
    );
  }
  if (!user) return null;

  const featuredCases = Object.values(caseStudies).flat().slice(0, 3);

  return (
    <section className="bg-neutral-50 min-h-[80vh] py-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="bg-black text-white rounded-3xl p-8 md:p-10 flex items-center justify-between flex-wrap gap-4 overflow-hidden relative">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="relative flex items-center gap-4">
            {user.picture && (
              <img src={user.picture} alt={user.username || user.email} className="w-14 h-14 rounded-full border-2 border-teal-400" />
            )}
            <div>
              <p className="text-teal-400 text-xs font-bold tracking-widest">MEMBER PORTAL</p>
              <h1 className="mt-1 text-2xl md:text-3xl font-extrabold">Welcome back, {user.username || user.email}</h1>
              <p className="mt-1 text-neutral-300 text-sm">
                {user.email}
                {user.full_role ? ` · ${user.full_role}` : ""}
                {user.auth_provider === "google" ? " · Google" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={signOut}
            className="relative inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-2 rounded-full transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-neutral-100">
            <ShieldCheck className="w-6 h-6 text-teal-500" />
            <h3 className="mt-3 font-bold text-neutral-900">Your account is secure</h3>
            <p className="mt-2 text-sm text-neutral-600">
              {user.auth_provider === "google"
                ? "Signed in with Google (managed OAuth)."
                : "Two-factor via email OTP will activate once SMTP is configured."}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-neutral-100">
            <FileText className="w-6 h-6 text-teal-500" />
            <h3 className="mt-3 font-bold text-neutral-900">Latest insights</h3>
            <p className="mt-2 text-sm text-neutral-600">{insights.length} research notes ready for you.</p>
            <Link to="/library" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-teal-600 hover:gap-2 transition-all">
              Open library <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-neutral-100">
            <Briefcase className="w-6 h-6 text-teal-500" />
            <h3 className="mt-3 font-bold text-neutral-900">Featured case studies</h3>
            <p className="mt-2 text-sm text-neutral-600">{Object.values(caseStudies).flat().length} client stories.</p>
            <Link to="/services" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-teal-600 hover:gap-2 transition-all">
              Explore <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-neutral-100 p-6">
          <h3 className="font-bold text-neutral-900">Recommended for you</h3>
          <ul className="mt-4 space-y-3">
            {featuredCases.map((c) => (
              <li key={c.slug}>
                <Link to={`/case-studies/${c.slug}`} className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 transition-colors">
                  <div>
                    <div className="text-xs font-bold text-teal-600">{c.client}</div>
                    <div className="font-semibold text-neutral-900">{c.title}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-neutral-400" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default MemberPortal;
